"use client";
import { useCallback, useState } from "react";
import axios from "axios";
import { useSession } from "next-auth/react";
import { PrismicRichText } from "@prismicio/react";

import cn from "@/utils/cn";
import { useZStore } from "@/utils/z-store";
import TextComponent from "@/app/_components/TextComponent";
import trackGAEvent from "@/utils/ga";

import BillingToggle from "./_components/BillingToggle";
import { PricingPlanComponentProp } from "./utils";
import { PricingPageDocumentData } from "../../../prismicio-types";

type PricingPlanProp = {
  items: PricingPlanComponentProp["slice"]["items"];
  title: PricingPageDocumentData["hero_title"];
  description: PricingPageDocumentData["hero_description"];
};

type PlanKey = "BASIC" | "STANDARD" | "PREMIUM";

type Plan = {
  key: PlanKey;
  name: string;
  tagline: string;
  isFree?: boolean;
  popular?: boolean;
  monthlyPrice: string;
  annualPrice: string;
  features: string[];
  cta: string;
};

const PLANS: Plan[] = [
  {
    key: "BASIC",
    name: "Basic",
    tagline: "Ideal for freelancers and startups",
    isFree: true,
    monthlyPrice: "Free",
    annualPrice: "Free",
    features: [
      "Email audits & analytics",
      "Performance tracking",
      "Industry benchmarks",
      "API Integrations",
    ],
    cta: "Start for free",
  },
  {
    key: "STANDARD",
    name: "Standard",
    tagline: "For marketer & business owners",
    popular: true,
    monthlyPrice: "35",
    annualPrice: "20",
    features: [
      "Team collaboration",
      "Custom dashboards",
      "Custom embedded forms",
      "Advanced metric cards",
      "Multiple API connections",
    ],
    cta: "Select plan",
  },
  {
    key: "PREMIUM",
    name: "Premium",
    tagline: "For agencies and teams",
    monthlyPrice: "60",
    annualPrice: "36",
    features: [
      "Generate leads",
      "Unlimited users",
      "Unlimited workspaces",
      "White-label branding",
      "AI suggestions",
    ],
    cta: "Select plan",
  },
];

const FeatureBadge = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    className={className}
  >
    <path
      d="M7.01412 1.74916C7.54306 1.16841 8.45679 1.16841 8.98572 1.74916L9.66586 2.49598C9.93312 2.7895 10.3173 2.94863 10.7139 2.9301L11.7229 2.88294C12.5075 2.84627 13.1537 3.49242 13.1171 4.27707L13.0699 5.28608C13.0513 5.68263 13.2105 6.06682 13.504 6.33412L14.2508 7.01424C14.8316 7.54318 14.8316 8.45691 14.2508 8.98584L13.504 9.66598C13.2105 9.93324 13.0513 10.3174 13.0699 10.714L13.1171 11.723C13.1537 12.5076 12.5075 13.1538 11.7229 13.1172L10.7139 13.07C10.3173 13.0514 9.93312 13.2106 9.66586 13.5041L8.98572 14.2509C8.45679 14.8317 7.54306 14.8317 7.01412 14.2509L6.33399 13.5041C6.0667 13.2106 5.68251 13.0514 5.28595 13.07L4.27694 13.1172C3.4923 13.1538 2.84615 12.5076 2.88282 11.723L2.92997 10.714C2.94851 10.3174 2.78937 9.93324 2.49586 9.66598L1.74904 8.98584C1.16829 8.45691 1.16829 7.54318 1.74904 7.01424L2.49586 6.33412C2.78937 6.06682 2.94851 5.68263 2.92997 5.28608L2.88282 4.27706C2.84615 3.49242 3.4923 2.84627 4.27695 2.88294L5.28595 2.9301C5.68251 2.94863 6.0667 2.7895 6.33399 2.49598L7.01412 1.74916Z"
      fill="#EDEDED"
      stroke="#444A6D"
      strokeWidth="1.06667"
    />
    <path
      d="M6 7.99996L7.33333 9.33329L10 6.66663"
      stroke="#444A6D"
      strokeWidth="1.06667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type PlanCardProps = {
  plan: Plan;
  isMonthly: boolean;
  isCurrent: boolean;
  onSelect: () => void;
};

const PlanCard = ({ plan, isMonthly, isCurrent, onSelect }: PlanCardProps) => {
  const price = isMonthly ? plan.monthlyPrice : plan.annualPrice;
  const isPopular = !!plan.popular;

  const cardContent = (
    <div className="flex h-full flex-col px-5 pb-5 pt-5 xl:px-[26px] xl:pb-[26px] xl:pt-[28px]">
      <div>
        <TextComponent
          type="h3"
          className="text-[15px] font-semibold leading-[22px] text-kd-secondary-1 xl:text-[18px] xl:leading-[32px]"
        >
          {plan.name}
        </TextComponent>
        <TextComponent
          type="body"
          className="text-[12px] font-medium leading-[18px] text-kd-lte-blue xl:text-[14px] xl:leading-[32px]"
        >
          {plan.tagline}
        </TextComponent>
      </div>

      <div className="mt-3 flex items-baseline text-kd-lte-blue xl:mt-[26px]">
        {plan.isFree ? (
          <TextComponent
            type="h2"
            className="text-[28px] font-semibold leading-[28px] text-kd-lte-blue xl:text-[40px] xl:leading-[32px]"
          >
            {price}
          </TextComponent>
        ) : (
          <>
            <TextComponent
              type="h2"
              className="text-[28px] font-semibold leading-[28px] text-kd-lte-blue xl:text-[40px] xl:leading-[32px]"
            >
              <sup className="text-[16px] xl:text-[24px]">$</sup>
              {price}
            </TextComponent>
            <TextComponent
              type="body"
              className="ml-1 text-[14px] font-semibold leading-[22px] text-kd-lte-blue xl:text-[18px] xl:leading-[32px]"
            >
              /month
            </TextComponent>
          </>
        )}
      </div>

      <ul className="mt-3 flex-1 space-y-1.5 xl:mt-[22px] xl:space-y-2.5">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-1.5">
            <FeatureBadge className="mt-[2px] h-3.5 w-3.5 flex-shrink-0 xl:h-4 xl:w-4" />
            <TextComponent
              type="body"
              className="text-[12px] font-normal leading-[120%] tracking-[-0.01em] text-kd-gray-text xl:text-[14px]"
            >
              {feature}
            </TextComponent>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onSelect}
        disabled={isCurrent}
        className={cn(
          "mt-4 inline-flex w-full items-center justify-center rounded-[10px] bg-kd-lte-blue px-3 py-2 transition-colors hover:bg-kd-lte-blue/90 xl:mt-[26px] xl:rounded-[12px] xl:px-4 xl:py-3",
          isCurrent && "cursor-not-allowed opacity-60 hover:bg-kd-lte-blue",
        )}
      >
        <TextComponent
          type="button"
          className="text-[13px] font-semibold leading-5 text-white xl:text-[16px] xl:leading-6"
        >
          {isCurrent ? "Your current plan" : plan.cta}
        </TextComponent>
      </button>
    </div>
  );

  if (isPopular) {
    return (
      <div className="mx-auto w-full max-w-[264px] rounded-[20px] bg-kd-lte-blue xl:max-w-[352px]">
        <div className="flex h-9 items-center justify-center xl:h-10">
          <TextComponent
            type="small"
            className="text-[11px] font-semibold uppercase leading-[115%] tracking-[0.08em] text-white xl:text-[12px]"
          >
            Most Popular Plan
          </TextComponent>
        </div>
        <div className="rounded-[20px] border border-kd-lte-blue bg-white">
          {cardContent}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[264px] rounded-[20px] border border-[#EDF2F6] bg-white xl:max-w-[352px]">
      {cardContent}
    </div>
  );
};

const PricingPlans = ({ title, description }: PricingPlanProp) => {
  // Annual is the default view — it's the better-value option we want to lead with.
  const [isMonthly, setIsMonthly] = useState(false);
  const { data, actions } = useZStore();
  const { data: session } = useSession();

  const handlePlanSelection = useCallback(
    async (planName: PlanKey, billingCycle: "MONTHLY" | "ANNUAL") => {
      try {
        const response = await axios.post("/api/subscription", {
          planName,
          billingCycle,
        });

        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error("No checkout URL returned");
        }
      } catch (error) {
        console.error("Error creating checkout session:", error);
      }
    },
    [],
  );

  const handleFreePlanSelection = useCallback(async () => {
    try {
      const response = await axios.post("/api/subscription/free");
      // Clear any persisted modal / payment state so the dashboard doesn't
      // re-open a stale billing modal after the redirect.
      actions.closeModal();
      actions.clearPaymentSuccessSessionId();
      actions.clearPendingCheckout();
      window.location.href = response.data?.redirect || "/dashboard/home";
    } catch (error) {
      console.error("Error activating free plan:", error);
    }
  }, [actions]);

  const handleSelect = (plan: Plan) => {
    const billingCycle: "MONTHLY" | "ANNUAL" = isMonthly ? "MONTHLY" : "ANNUAL";
    // Canonical funnel event (Tracking tab): plan chosen.
    trackGAEvent("plan_selected", { plan: plan.key, interval: billingCycle });

    if (session?.user) {
      if (plan.isFree) {
        handleFreePlanSelection();
      } else {
        handlePlanSelection(plan.key, billingCycle);
      }
    } else {
      actions.setPendingCheckout({
        planName: plan.key,
        billingCycle,
      });
      actions.openModal({ modalName: "sign-up" });
    }
  };

  return (
    <div className="bg-white px-5 py-14 sm:py-16 md:px-9 lg:px-20 xl:py-20">
      <div className="mx-auto max-w-[1120px]">
        <div className="mx-auto max-w-[720px] text-center">
          <TextComponent
            type="h1"
            className="text-[28px] font-semibold leading-[1.2] text-kd-lte-blue sm:text-[32px] md:text-[36px] xl:text-[40px] xl:leading-[51px]"
          >
            <PrismicRichText
              field={title}
              fallback={<p>Plans &amp; Pricing</p>}
            />
          </TextComponent>
          <TextComponent
            type="body"
            className="mx-auto mt-3 text-[13px] font-normal leading-[22px] text-kd-gray-text sm:text-[14px] xl:text-[14px] xl:leading-[24px]"
          >
            <PrismicRichText
              field={description}
              fallback={
                <p>
                  Choose the plan that fits your needs. All plans include core
                  features to get you started, with options to scale as you grow.
                  No hidden fees and the flexibility to change anytime.
                </p>
              }
            />
          </TextComponent>
        </div>

        <div className="mt-7 flex flex-col items-center sm:mt-8 xl:mt-10">
          <BillingToggle isMonthly={isMonthly} setIsMonthly={setIsMonthly} />
          <TextComponent
            type="caption"
            className="mt-2 text-[13px] font-medium leading-[115%] text-kd-lte-blue xl:text-[14px]"
          >
            Save 40% off on annual payments
          </TextComponent>
        </div>

        <div className="mt-10 grid grid-cols-1 items-end gap-5 sm:mt-12 sm:gap-6 lg:grid-cols-3 lg:gap-5 xl:gap-6">
          {PLANS.map((plan) => {
            const isCurrent =
              data.subscriptionPlan?.toUpperCase().includes(plan.key) ?? false;
            return (
              <PlanCard
                key={plan.key}
                plan={plan}
                isMonthly={isMonthly}
                isCurrent={isCurrent}
                onSelect={() => handleSelect(plan)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PricingPlans;
